import { useState, type MouseEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useApiClient } from "../../api/ApiClientContext";
import type { MemberCredential, SharingDetail } from "../../api/types";
import { ApiError } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { BulkCredentialDialog, type BulkCredentialFailure } from "../../components/BulkCredentialDialog";
import { OneTimeCredentialDialog } from "../../components/OneTimeCredentialDialog";
import { useRowMenu } from "../../hooks/useRowMenu";
import { removeAction, resetPasswordAction } from "../../utils/memberActions";

export interface StaffSharingSettingsTabProps {
  code: string;
  detail: SharingDetail;
  onRefresh: () => Promise<void>;
}

/** Members, the IP allowlist and closing the sharing: everything that is not file browsing, the second tab of the sharing detail page. */
export function StaffSharingSettingsTab({ code, detail, onRefresh }: StaffSharingSettingsTabProps) {
  const apiClient = useApiClient();
  const rowMenu = useRowMenu();

  const [credential, setCredential] = useState<MemberCredential | null>(null);
  const [credentialTitle, setCredentialTitle] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [memberActionError, setMemberActionError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);

  const [bulkResetting, setBulkResetting] = useState(false);
  const [bulkCredentialsOpen, setBulkCredentialsOpen] = useState(false);
  const [bulkCredentials, setBulkCredentials] = useState<MemberCredential[]>([]);
  const [bulkFailures, setBulkFailures] = useState<BulkCredentialFailure[]>([]);

  const [bulkRemoveIds, setBulkRemoveIds] = useState<string[]>([]);
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);
  const [bulkRemoveError, setBulkRemoveError] = useState<string | null>(null);

  // The lazy initializer syncs from the server only once, on the first
  // render: refreshes after other actions (adding a member, and so on)
  // must not clobber an unsaved allowlist edit (review item). The `?? []`
  // is defensive: the BFF sends a JSON null, not [], for an unrestricted
  // sharing's nil Go slice.
  const [allowlistText, setAllowlistText] = useState(() => (detail.allowedIP ?? []).join("\n"));
  const [allowlistSaving, setAllowlistSaving] = useState(false);
  const [allowlistError, setAllowlistError] = useState<string | null>(null);

  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // `?? []` is defensive (manager report: a fresh sharing's BFF response
  // used to send a JSON null, not [], for its no-members Go nil slice,
  // and detail.members.map(...) crashed the whole page with no error
  // boundary).
  const members = detail.members ?? [];
  const memberIds = members.map((member) => member.id);
  const allSelected = memberIds.length > 0 && memberIds.every((id) => selected.has(id));
  const someSelected = memberIds.some((id) => selected.has(id)) && !allSelected;

  function toggleSelect(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((current) => (current.size === memberIds.length ? new Set() : new Set(memberIds)));
  }

  async function handleAddMember() {
    setMemberActionError(null);
    setAddingMember(true);
    try {
      const result = await apiClient.addMember(code);
      setCredentialTitle("New member credentials");
      setCredential(result);
      await onRefresh();
    } catch (caught) {
      setMemberActionError(caught instanceof ApiError ? caught.message : "Could not add a member.");
    } finally {
      setAddingMember(false);
    }
  }

  async function handleResetPassword(id: string) {
    setMemberActionError(null);
    setResettingId(id);
    try {
      const result = await apiClient.resetMemberPassword(code, id);
      setCredentialTitle(`New password for ${id}`);
      setCredential(result);
    } catch (caught) {
      setMemberActionError(caught instanceof ApiError ? caught.message : "Could not reset the password.");
    } finally {
      setResettingId(null);
    }
  }

  async function handleRemoveMember() {
    if (!removeTarget) {
      return;
    }
    setRemoveError(null);
    try {
      await apiClient.removeMember(code, removeTarget);
      setRemoveTarget(null);
      await onRefresh();
    } catch (caught) {
      setRemoveError(caught instanceof ApiError ? caught.message : "Could not remove the member.");
    }
  }

  function buildMemberActions(id: string) {
    return [resetPasswordAction(() => void handleResetPassword(id)), removeAction(() => setRemoveTarget(id))];
  }

  function openActions(event: MouseEvent<HTMLElement>) {
    setActionsAnchor(event.currentTarget);
  }

  function closeActions() {
    setActionsAnchor(null);
  }

  /**
   * "Reset password" on the selection: one call per member, sequentially
   * (a bounded, small workload - a sharing's member count), then one
   * dialog listing every new credential plus any failure, instead of one
   * one-time dialog per member.
   */
  async function handleBulkReset() {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      return;
    }
    setBulkResetting(true);
    const credentials: MemberCredential[] = [];
    const failures: BulkCredentialFailure[] = [];
    for (const id of ids) {
      try {
        credentials.push(await apiClient.resetMemberPassword(code, id));
      } catch (caught) {
        failures.push({
          id,
          message: caught instanceof ApiError ? caught.message : "Could not reset the password.",
        });
      }
    }
    setBulkCredentials(credentials);
    setBulkFailures(failures);
    setBulkCredentialsOpen(true);
    setBulkResetting(false);
    setSelected(new Set());
  }

  function openBulkRemoveConfirm() {
    setBulkRemoveIds(Array.from(selected));
    setBulkRemoveError(null);
    setBulkRemoveConfirmOpen(true);
  }

  async function handleBulkRemoveConfirmed() {
    setBulkRemoveError(null);
    const failures: string[] = [];
    for (const id of bulkRemoveIds) {
      try {
        await apiClient.removeMember(code, id);
      } catch (caught) {
        failures.push(`${id}: ${caught instanceof ApiError ? caught.message : "Could not remove the member."}`);
      }
    }
    if (failures.length > 0) {
      setBulkRemoveError(
        `Could not remove ${failures.length} of ${bulkRemoveIds.length} members: ${failures.join("; ")}`,
      );
    } else {
      setBulkRemoveConfirmOpen(false);
      setSelected(new Set());
    }
    await onRefresh();
  }

  async function handleSaveAllowlist() {
    setAllowlistError(null);
    setAllowlistSaving(true);
    try {
      const cidrs = allowlistText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== "");
      const sharing = await apiClient.setAllowlist(code, { cidrs });
      setAllowlistText((sharing.allowedIP ?? []).join("\n"));
      await onRefresh();
    } catch (caught) {
      setAllowlistError(caught instanceof ApiError ? caught.message : "Could not update the allowlist.");
    } finally {
      setAllowlistSaving(false);
    }
  }

  async function handleCloseSharing() {
    setCloseError(null);
    try {
      await apiClient.closeSharing(code);
      setCloseConfirmOpen(false);
      await onRefresh();
    } catch (caught) {
      setCloseError(caught instanceof ApiError ? caught.message : "Could not close the sharing.");
    }
  }

  const menuRow = rowMenu.menu ? members.find((member) => member.id === rowMenu.menu!.rowKey) : undefined;
  const menuRowActions = menuRow ? buildMemberActions(menuRow.id) : [];

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" component="h2">
            Members
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              endIcon={<ExpandMoreIcon fontSize="small" />}
              onClick={openActions}
              disabled={bulkResetting}
            >
              Actions
            </Button>
            <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActions}>
              <MenuItem
                disabled={selected.size === 0 || bulkResetting}
                onClick={() => {
                  closeActions();
                  void handleBulkReset();
                }}
              >
                Reset password
              </MenuItem>
              <MenuItem
                disabled={selected.size === 0}
                onClick={() => {
                  closeActions();
                  openBulkRemoveConfirm();
                }}
              >
                Remove
              </MenuItem>
            </Menu>
            <Button variant="contained" onClick={() => void handleAddMember()} disabled={addingMember}>
              {addingMember ? "Adding..." : "Add member"}
            </Button>
          </Stack>
        </Stack>

        {memberActionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {memberActionError}
          </Alert>
        )}

        {members.length === 0 ? (
          <Typography color="text.secondary">No members yet.</Typography>
        ) : (
          <Table aria-label="Members" size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    slotProps={{ input: { "aria-label": "Select all members" } }}
                  />
                </TableCell>
                <TableCell>Member ID</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => {
                const actions = buildMemberActions(member.id);
                return (
                  <TableRow
                    key={member.id}
                    hover
                    tabIndex={0}
                    onContextMenu={(event) => rowMenu.handleContextMenu(event, member.id, actions)}
                    onKeyDown={(event) => rowMenu.handleKeyDown(event, member.id, actions)}
                    onTouchStart={(event) => rowMenu.handleTouchStart(event, member.id, actions)}
                    onTouchEnd={rowMenu.clearLongPress}
                    onTouchMove={rowMenu.clearLongPress}
                    onTouchCancel={rowMenu.clearLongPress}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(member.id)}
                        onChange={() => toggleSelect(member.id)}
                        slotProps={{ input: { "aria-label": `Select ${member.id}` } }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {member.id}
                      {resettingId === member.id && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Resetting...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        aria-label={`More actions for ${member.id}`}
                        size="small"
                        onClick={(event) => rowMenu.openAtElement(member.id, event.currentTarget)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {rowMenu.menu && menuRow && (
              <Menu
                open
                onClose={rowMenu.close}
                anchorEl={rowMenu.menu.anchorEl ?? undefined}
                anchorReference={rowMenu.menu.anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={rowMenu.menu.anchorPosition ?? undefined}
              >
                {menuRowActions.map((action) => (
                  <MenuItem
                    key={action.key}
                    onClick={() => {
                      rowMenu.close();
                      action.onSelect();
                    }}
                  >
                    {action.label}
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Table>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          IP allowlist
        </Typography>
        {allowlistError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {allowlistError}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Allowed CIDRs"
            helperText="One CIDR per line, for example 203.0.113.0/24. Leave empty to remove all restrictions."
            multiline
            minRows={2}
            fullWidth
            value={allowlistText}
            onChange={(event) => setAllowlistText(event.target.value)}
          />
          <Box>
            <Button variant="contained" onClick={() => void handleSaveAllowlist()} disabled={allowlistSaving}>
              {allowlistSaving ? "Saving..." : "Save allowlist"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/*
        Danger zone (manager decision, 2026-09-11): every destructive,
        sharing-wide action lives in its own visually separated section at
        the bottom, not mixed in with the Members/allowlist cards above.
        colorTokens.danger (theme palette error.main, #B42318) on white is
        roughly 6.6:1, comfortably past WCAG AA's 4.5:1 for normal text.
      */}
      <Box
        component="section"
        aria-labelledby="danger-zone-heading"
        sx={{
          border: "1px solid",
          borderColor: "error.main",
          borderRadius: 1,
          p: 3,
        }}
      >
        <Typography
          id="danger-zone-heading"
          variant="h6"
          component="h2"
          sx={{ color: "error.main", mb: 2 }}
        >
          Danger zone
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Close sharing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every member loses access immediately. Files are kept.
            </Typography>
          </Box>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setCloseConfirmOpen(true)}
            disabled={detail.closed}
            sx={{ flexShrink: 0 }}
          >
            {detail.closed ? "Already closed" : "Close sharing"}
          </Button>
        </Stack>
      </Box>

      <OneTimeCredentialDialog
        open={credential !== null}
        title={credentialTitle}
        credential={credential}
        onClose={() => setCredential(null)}
      />

      <BulkCredentialDialog
        open={bulkCredentialsOpen}
        title={
          bulkCredentials.length === 1
            ? "New password for 1 member"
            : `New passwords for ${bulkCredentials.length} members`
        }
        credentials={bulkCredentials}
        failures={bulkFailures}
        onClose={() => {
          setBulkCredentialsOpen(false);
          setBulkCredentials([]);
          setBulkFailures([]);
        }}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove member"
        description={`This deletes ${removeTarget}'s account. This cannot be undone.`}
        confirmLabel="Remove member"
        destructive
        error={removeError}
        onConfirm={() => handleRemoveMember()}
        onClose={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
      />

      <ConfirmDialog
        open={bulkRemoveConfirmOpen}
        title="Remove members"
        description={`This deletes ${bulkRemoveIds.length} member account${bulkRemoveIds.length === 1 ? "" : "s"}. This cannot be undone.`}
        confirmLabel="Remove members"
        destructive
        error={bulkRemoveError}
        onConfirm={() => handleBulkRemoveConfirmed()}
        onClose={() => {
          setBulkRemoveConfirmOpen(false);
          setBulkRemoveError(null);
        }}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="Close sharing"
        description={
          <>
            This removes every member of <strong>{detail.displayName}</strong>. Files are kept.
          </>
        }
        confirmLabel="Close sharing"
        confirmationText="DELETE"
        uppercaseInput
        destructive
        error={closeError}
        onConfirm={() => handleCloseSharing()}
        onClose={() => {
          setCloseConfirmOpen(false);
          setCloseError(null);
        }}
      />
    </Stack>
  );
}
